import { useState, useEffect } from 'react';

interface ConsultModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConsultModal({ isOpen, onClose }: ConsultModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    solution: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.name || !formData.phone) {
      alert('请填写必填项');
      return;
    }

    // 验证手机号格式
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('请输入正确的手机号码');
      return;
    }

    // 验证邮箱格式（如果填写了）
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('请输入正确的邮箱地址');
        return;
      }
    }

    // 验证需求描述长度
    if (formData.message && formData.message.length > 500) {
      alert('需求描述不能超过500字');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4jc0ckucjmktht587bg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          solution: '',
          message: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('提交失败:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩层 */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* 弹窗内容 */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
          aria-label="关闭"
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>

        {/* 头部 */}
        <div className="bg-gradient-to-br from-blue-600 to-teal-600 px-8 py-10 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm">
              <i className="ri-customer-service-2-fill text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold">咨询顾问</h2>
          </div>
          <p className="text-white/90 text-lg">
            填写您的信息，我们的专业顾问将在24小时内与您联系
          </p>
        </div>

        {/* 表单 */}
        <form 
          id="consult-form"
          data-readdy-form
          onSubmit={handleSubmit} 
          className="p-8"
        >
          <div className="space-y-6">
            {/* 姓名 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                您的姓名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="请输入您的姓名"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
              />
            </div>

            {/* 公司名称 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                公司名称
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="请输入公司名称"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
              />
            </div>

            {/* 联系电话 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                联系电话 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="请输入11位手机号码"
                maxLength={11}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
              />
            </div>

            {/* 邮箱 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                电子邮箱
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="请输入您的邮箱地址"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
              />
            </div>

            {/* 需求描述 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                需求描述
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="请简要描述您的需求或遇到的问题（限500字）"
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none text-sm"
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.message.length}/500
              </div>
            </div>
          </div>

          {/* 提交状态提示 */}
          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
              <div>
                <p className="text-green-800 font-semibold">提交成功！</p>
                <p className="text-green-700 text-sm">我们的顾问将在24小时内与您联系</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
              <div>
                <p className="text-red-800 font-semibold">提交失败</p>
                <p className="text-red-700 text-sm">请稍后重试或直接联系我们</p>
              </div>
            </div>
          )}

          {/* 提交按钮 */}
          <div className="mt-8 flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-blue-600 to-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-fill"></i>
                  提交申请
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
            >
              取消
            </button>
          </div>

          {/* 提示信息 */}
          <p className="mt-6 text-center text-sm text-gray-500">
            <i className="ri-lock-fill mr-1"></i>
            您的信息将被严格保密，仅用于业务咨询
          </p>
        </form>
      </div>
    </div>
  );
}
